import React, { useEffect, useMemo, useState } from "react";
import ApiService from "../../../service/ApiService";
import { apiPaths } from "../../../service/apiPaths";
import CustomDropdown from "../../../components/DropDown";

const ManageVideoCategories: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [name, setName] = useState("");
  const [sub, setSub] = useState("");
  const [selectedId, setSelectedId] = useState<string>("");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const selected = useMemo(() => items.find((i) => i._id === selectedId), [items, selectedId]);
  const categoryOptions = useMemo(() => items.map((i) => ({ id: i._id, name: i.name })), [items]);

  const load = async () => {
    const data = await ApiService({ method: "GET", endpoint: apiPaths.videoCategories });
    setItems(Array.isArray(data) ? data : []);
  };

  const create = async () => {
    if (!name.trim()) return;
    await ApiService({ method: "POST", endpoint: apiPaths.videoCategoriesCreate, data: { name } });
    setName("");
    await load();
  };

  const addSub = async () => {
    if (!selectedId || !sub.trim()) return;
    await ApiService({ method: "POST", endpoint: `${apiPaths.videoCategories}/${selectedId}/subcategories`, data: { name: sub } });
    setSub("");
    await load();
  };

  const removeSub = async (categoryId: string, sc: string) => {
    await ApiService({ method: "DELETE", endpoint: `${apiPaths.videoCategories}/${categoryId}/subcategories`, data: { name: sc } });
    await load();
  };

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold mb-4">Manage Video Categories</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">New Category</label>
          <div className="flex gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} className="flex-1 border rounded px-3 py-2" placeholder="Category name" />
            <button onClick={create} className="px-3 py-2 bg-blue-600 text-white rounded">Add</button>
          </div>
        </div>
        <div>
          <CustomDropdown
            label="Select Category"
            options={categoryOptions}
            defaultOptionId={selectedId}
            setDefaultOptionId={setSelectedId}
            displayProperty="name"
            value={selectedId}
            style="flex items-center border h-10 rounded-lg shadow-none relative text-sm border-border text-fontColor w-full"
            showStyle="text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Add Subcategory</label>
          <div className="flex gap-2">
            <input value={sub} onChange={(e) => setSub(e.target.value)} className="flex-1 border rounded px-3 py-2" placeholder="Subcategory name" />
            <button onClick={addSub} disabled={!selectedId} className="px-3 py-2 bg-green-600 text-white rounded disabled:opacity-50">Add</button>
          </div>
        </div>
      </div>

      {/* All categories in a collapsible tree */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold mb-2">All Categories</h3>
        <ul className="border rounded divide-y">
          {items.map((cat) => {
            const isOpen = expandedIds.has(cat._id);
            const count = (cat.subcategories || []).length;
            const isSelected = selectedId === cat._id;
            return (
              <li key={cat._id} className="">
                <button
                  type="button"
                  onClick={() => { setSelectedId(cat._id); toggleExpanded(cat._id); }}
                  className={`w-full flex items-center justify-between px-3 py-2 text-left hover:bg-gray-50 ${isSelected ? "bg-blue-50" : ""}`}
                >
                  <div className="flex items-center gap-2">
                    <svg className={`h-4 w-4 text-gray-600 transition-transform ${isOpen ? "rotate-90" : ""}`} viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 111.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium">{cat.name}</span>
                    {isSelected && <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded">Selected</span>}
                  </div>
                  <span className="text-xs text-gray-500">{count} sub{count === 1 ? "" : "s"}</span>
                </button>
                {isOpen && (
                  <div className="pl-6 pr-3 pb-3">
                    {count === 0 ? (
                      <span className="text-sm text-gray-500">No subcategories</span>
                    ) : (
                      <ul className="border rounded divide-y">
                        {(cat.subcategories || []).map((sc: string) => (
                          <li key={sc} className="flex items-center justify-between px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                              <span className="text-sm">{sc}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeSub(cat._id, sc)}
                              className="text-xs text-gray-500 hover:text-red-600"
                              title="Remove subcategory"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default ManageVideoCategories;


